<template>
  <b-modal ref="modal" size="lg" hide-footer :title="order ? `Pack ${order.order_number}` : 'Pack produced stock'" @hidden="reset">
    <p class="text-muted small mb-3">
      Fill bags, bottles… from a product you produced. Its stock goes down, the packs go up at its cost
      plus their packaging, and every movement shows in Inventory Activity.
    </p>

    <!-- Which product was produced (standalone packing): search the inventory -->
    <div v-if="!fixedProduct && !orderId" class="form-group">
      <label class="mb-1">Product you produced</label>
      <input v-model="productSearch" type="text" class="form-control" placeholder="Search inventory, e.g. High Yield Dairy Meal" @input="searchSource">
      <div v-if="sourceResults.length" class="list-group mt-1" style="max-height:240px; overflow-y:auto;">
        <a v-for="s in sourceResults" :key="s.id" href="#" class="list-group-item list-group-item-action d-flex justify-content-between" @click.prevent="pickSource(s)">
          <span>{{ s.product }}</span><small class="text-muted">{{ fmt(s.remaining_units) }} {{ s.measuring_unit }} in stock</small>
        </a>
      </div>
    </div>

    <div v-if="loading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>

    <template v-else-if="options">
      <div class="d-flex flex-wrap mb-3" style="gap:24px;">
        <div><small class="text-muted d-block">Packing from</small><strong class="text-dark">{{ options.product.product }}</strong></div>
        <div v-if="order"><small class="text-muted d-block">This batch</small><strong>{{ fmt(order.produced) }} {{ unit }} made · {{ fmt(order.remaining) }} {{ unit }} left to pack</strong></div>
        <div><small class="text-muted d-block">In stock</small><strong>{{ fmt(options.product.remaining_units) }} {{ unit }}</strong></div>
        <div><small class="text-muted d-block">Cost</small><strong>{{ options.product.wholesale_ppu == null ? 'not set' : money(options.product.wholesale_ppu) + ' / ' + unit }}</strong></div>
      </div>

      <div v-if="order && !packingOpen" class="alert alert-secondary py-2">Packing of this order is finished.</div>

      <template v-if="!order || packingOpen">
        <label class="mb-1">Packs filled</label>
        <div class="border rounded">
          <div v-for="p in lines" :key="p.id" class="d-flex flex-wrap align-items-center px-3 py-2 border-bottom" style="gap:12px;">
            <div style="flex:1; min-width:180px;">
              <strong>{{ p.product && p.product.product }}</strong>
              <small class="text-muted d-block">{{ fmt(p.content_quantity) }} {{ p.content_unit }} per pack · {{ fmt(p.product && p.product.remaining_units) }} in stock</small>
              <small v-for="k in kitOf(p)" :key="k.product_id" class="d-block" :class="Number(p.quantity) > 0 && kitShort(p, k) ? 'text-danger' : 'text-muted'">
                uses {{ fmt(k.quantity) }} × {{ k.name }} per pack<span v-if="Number(p.quantity) > 0"> → {{ fmt(Number(p.quantity) * k.quantity) }} ({{ fmt(k.stock) }} in stock)</span>
              </small>
            </div>
            <div class="input-group" style="max-width:160px;">
              <input v-model.number="p.quantity" type="number" min="0" step="1" class="form-control" placeholder="0">
              <div class="input-group-append"><span class="input-group-text">packs</span></div>
            </div>
            <div class="w-100 d-flex justify-content-end" style="gap:12px;">
              <button type="button" class="btn btn-link btn-sm p-0" @click="editSize(p)">Edit</button>
              <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="removeSize(p)">Remove</button>
            </div>
          </div>
          <div v-if="!lines.length" class="px-3 py-2 text-muted small">No pack sizes yet — add one below.</div>
        </div>
        <small v-if="summary" class="d-block mt-1" :class="summary.over ? 'text-danger' : 'text-muted'">{{ summary.text }}</small>

        <!-- Add a pack size on the spot -->
        <div class="mt-2">
          <button v-if="!adding.open" type="button" class="btn btn-link p-0" @click="adding.open = true"><i class="bi bi-plus-circle mr-1"></i>Add another pack size</button>
          <div v-else class="border rounded p-3">
            <div class="form-row">
              <div class="form-group col-md-6 position-relative">
                <label class="mb-1 small">Packed product (from inventory, counted per pack)</label>
                <input v-model="adding.search" type="text" class="form-control" :disabled="!!adding.editId" placeholder="e.g. Dairy Meal 50kg, Mango Juice 1L" @input="searchProducts('product')">
                <div v-if="adding.suggestions.length" class="list-group w-100 mt-1" style="max-height:220px; overflow-y:auto;">
                  <a v-for="s in adding.suggestions" :key="s.id" href="#" class="list-group-item list-group-item-action py-1" @click.prevent="pickProduct(s)">{{ s.product }} <small class="text-muted">({{ s.measuring_unit }})</small></a>
                </div>
              </div>
              <div class="form-group col-md-3">
                <label class="mb-1 small">Holds</label>
                <div class="input-group">
                  <input v-model.number="adding.content_quantity" type="number" min="0" step="any" class="form-control">
                  <div class="input-group-append"><span class="input-group-text">{{ unit }}</span></div>
                </div>
              </div>
              <div class="form-group col-md-3 position-relative">
                <label class="mb-1 small">Packaging per pack</label>
                <input v-model="adding.kitSearch" type="text" class="form-control" placeholder="bag, bottle, cap…" @input="searchProducts('kit')">
                <div v-if="adding.kitSuggestions.length" class="list-group w-100 mt-1" style="max-height:220px; overflow-y:auto;">
                  <a v-for="s in adding.kitSuggestions" :key="s.id" href="#" class="list-group-item list-group-item-action py-1" @click.prevent="pickKit(s)">{{ s.product }}</a>
                </div>
              </div>
            </div>
            <div v-for="(k, ki) in adding.kit" :key="k.product_id" class="d-flex align-items-center mb-1" style="gap:6px;">
              <input v-model.number="k.quantity" type="number" min="0.0001" step="any" class="form-control" style="max-width:90px;">
              <span class="small">× {{ k.name }} per pack</span>
              <button type="button" class="btn btn-link btn-sm text-danger p-0 ml-auto" @click="adding.kit.splice(ki, 1)"><i class="bi bi-x"></i></button>
            </div>
            <p v-if="adding.error" class="text-danger small mb-2">{{ adding.error }}</p>
            <div class="d-flex" style="gap:8px;">
              <button type="button" class="btn btn-primary" :disabled="adding.saving || !adding.product_id || !(adding.content_quantity > 0)" @click="saveSize">{{ adding.editId ? 'Save pack size' : 'Add pack size' }}</button>
              <button type="button" class="btn btn-outline-secondary" @click="adding = emptyAdding()">Cancel</button>
            </div>
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="mb-1">Notes</label>
          <input v-model="notes" type="text" class="form-control" maxlength="1000" placeholder="Optional, e.g. morning shift">
        </div>

        <p v-if="error" class="text-danger small mb-2">{{ error }}</p>
        <div class="d-flex justify-content-end" style="gap:8px;">
          <button type="button" class="btn btn-outline-secondary" @click="$refs.modal.hide()">Close</button>
          <button type="button" class="btn btn-primary" :disabled="saving || !canSave" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>Record packing
          </button>
        </div>

        <!-- Finish (or skip) the order's packing stage -->
        <div v-if="order" class="border-top mt-3 pt-3">
          <h6 class="text-dark mb-2">{{ order.has_sessions ? 'Finish packing' : 'Not packing this batch?' }}</h6>
          <template v-if="order.has_sessions && order.remaining > 0">
            <div class="custom-control custom-radio mb-1">
              <input id="pkKeep" v-model="finish.leftover" type="radio" value="keep" class="custom-control-input">
              <label class="custom-control-label" for="pkKeep">Keep the {{ fmt(order.remaining) }} {{ unit }} left as loose {{ options.product.product }}</label>
            </div>
            <div class="custom-control custom-radio mb-2">
              <input id="pkWaste" v-model="finish.leftover" type="radio" value="waste" class="custom-control-input">
              <label class="custom-control-label" for="pkWaste">Write it off (spilt / not usable)</label>
            </div>
            <input v-if="finish.leftover === 'waste'" v-model="finish.reason" type="text" class="form-control mb-2" maxlength="500" placeholder="Why? e.g. spilt during packing">
          </template>
          <small v-if="!order.has_sessions" class="text-muted d-block mb-2">Skipping keeps the whole batch as loose {{ options.product.product }}.</small>
          <button type="button" class="btn btn-outline-primary" :disabled="saving" @click="finishPacking">{{ order.has_sessions ? 'Finish packing' : 'Skip packing' }}</button>
        </div>
      </template>

      <!-- Recent packing -->
      <div v-if="recent.length" class="mt-4">
        <h6 class="text-dark mb-2">Recent packing</h6>
        <div v-for="r in recent" :key="r.id" class="border-top py-2 small">
          <div class="d-flex flex-wrap align-items-center" style="gap:8px;">
            <strong>{{ r.number }}</strong>
            <span class="text-muted">{{ formatDate(r.created_at) }} · {{ r.created_by && r.created_by.name }}</span>
            <span v-if="r.reversed_at" class="badge badge-secondary">Reversed</span>
            <button v-else-if="reversing !== r.id" type="button" class="btn btn-link btn-sm text-danger p-0 ml-auto" @click="startReverse(r)">Reverse</button>
          </div>
          <div class="text-muted">
            {{ fmt(r.source_quantity) }} {{ unit }} → <span v-for="(l, i) in r.lines" :key="l.id">{{ i ? ', ' : '' }}{{ fmt(l.packs) }} × {{ l.product && l.product.product }}</span>
            <span v-if="r.notes"> · {{ r.notes }}</span>
          </div>
          <div v-if="reversing === r.id" class="d-flex mt-2" style="gap:8px;">
            <input v-model="reverseReason" type="text" class="form-control" maxlength="500" placeholder="Why reverse it?">
            <button type="button" class="btn btn-danger" :disabled="!reverseReason.trim() || saving" @click="reverse(r)">Reverse</button>
            <button type="button" class="btn btn-outline-secondary" @click="reversing = null">Cancel</button>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';

/**
 * Packing (RepackService). open(productId?, orderId?):
 *  - orderId: pack that production order's loose batch (packing stage), then
 *    finish or skip it;
 *  - productId: pack that loose product's stock;
 *  - neither: pick any loose product that has pack sizes.
 */
export default {
  name: 'RepackModal',

  data() {
    return {
      fixedProduct: false,
      productId: null,
      orderId: null,
      sources: [],
      sourcesLoaded: false,
      productSearch: '',
      sourceResults: [],
      options: null,
      lines: [],
      notes: '',
      recent: [],
      loading: false,
      saving: false,
      error: null,
      reversing: null,
      reverseReason: '',
      finish: { leftover: 'keep', reason: '' },
      adding: this.emptyAdding(),
    };
  },

  computed: {
    order() {
      return this.options && this.options.order;
    },
    packingOpen() {
      return this.order && ['awaiting', 'in_progress'].includes(this.order.packing_status);
    },
    unit() {
      return (this.options && this.options.product.measuring_unit) || '';
    },
    limit() {
      const stock = Number((this.options && this.options.product.remaining_units) || 0);
      return this.order ? Math.min(stock, Number(this.order.remaining || 0)) : stock;
    },
    summary() {
      const used = this.lines.filter(p => Number(p.quantity) > 0);
      if (!used.length || used.some(p => p.content_unit !== this.unit)) return null;
      const r = n => Math.round(n * 1000) / 1000;
      const packed = used.reduce((t, p) => t + Number(p.quantity) * Number(p.content_quantity), 0);
      const what = this.order ? 'is left of this batch' : 'is in stock';
      if (packed - this.limit > 0.0001) return { over: true, text: `The packs hold ${r(packed)} ${this.unit} but only ${r(this.limit)} ${this.unit} ${what}.` };
      return { over: false, text: `Packs hold ${r(packed)} ${this.unit}; ${r(this.limit - packed)} ${this.unit} stays as ${this.options.product.product}.` };
    },
    canSave() {
      return this.lines.some(p => Number(p.quantity) > 0) && !(this.summary && this.summary.over);
    },
  },

  methods: {
    searchSource() {
      const q = (this.productSearch || '').trim();
      if (q.length < 2) { this.sourceResults = []; return; }
      clearTimeout(this.srcTimer);
      this.srcTimer = setTimeout(async () => {
        try {
          // Same finished-product search as the recipe form (any stocked product).
          const { data } = await axios.get('/api/manufacturing/boms/output-products', { params: { search: q } });
          this.sourceResults = (data || []).map(p => ({ ...p, measuring_unit: p.unit }));
        } catch (e) { this.sourceResults = []; }
      }, 300);
    },
    pickSource(s) {
      this.productId = s.id;
      this.productSearch = s.product;
      this.sourceResults = [];
      this.loadOptions(true);
    },

    emptyAdding() {
      return { open: false, editId: null, search: '', suggestions: [], product_id: null, content_quantity: null, kitSearch: '', kitSuggestions: [], kit: [], saving: false, error: null };
    },

    async open(productId = null, orderId = null) {
      this.reset();
      this.fixedProduct = !!productId;
      this.productId = productId;
      this.orderId = orderId;
      this.$refs.modal.show();
      if (productId || orderId) this.loadOptions(true);
    },

    reset() {
      Object.assign(this, {
        productId: null, orderId: null, options: null, productSearch: '', sourceResults: [], lines: [], notes: '', recent: [], error: null,
        reversing: null, reverseReason: '', sourcesLoaded: false, finish: { leftover: 'keep', reason: '' }, adding: this.emptyAdding(),
      });
    },

    async loadOptions(prefill = false) {
      if (!this.productId && !this.orderId) return;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/manufacturing/repacks/options', {
          params: { product_id: this.productId || undefined, production_order_id: this.orderId || undefined },
        });
        const typed = {};
        this.lines.forEach(l => { typed[l.id] = l.quantity; });
        this.options = data;
        this.productId = data.product.id;
        // First session of an order starts from the bags planned on it.
        const plan = prefill && data.order && !data.order.has_sessions ? (data.order.planned_packs || []) : [];
        // Nothing to pack into yet: open "add a pack size" straight away.
        if (!data.pack_sizes.length) this.adding.open = true;
        this.lines = data.pack_sizes.map(p => ({
          ...p,
          quantity: typed[p.id] !== undefined ? typed[p.id] : ((plan.find(l => l.pack_size_id === p.id) || {}).quantity || null),
        }));
        this.loadRecent();
      } catch (e) {
        this.error = this.message(e);
      } finally {
        this.loading = false;
      }
    },

    async loadRecent() {
      try {
        const params = this.orderId ? { production_order_id: this.orderId, limit: 10 } : { product_id: this.productId, limit: 5 };
        this.recent = (await axios.get('/api/manufacturing/repacks', { params })).data;
      } catch (e) { this.recent = []; }
    },

    kitOf(p) {
      const comps = (p.components || []).map(c => ({ product_id: c.product_id, name: c.product && c.product.product, quantity: Number(c.quantity), stock: Number((c.product && c.product.remaining_units) || 0) }));
      if (comps.length || !p.packaging_product) return comps;
      return [{ product_id: p.packaging_product.id, name: p.packaging_product.product, quantity: Number(p.packaging_quantity || 1), stock: Number(p.packaging_product.remaining_units || 0) }];
    },

    kitShort(p, k) {
      return Number(p.quantity) * k.quantity > k.stock;
    },

    async save() {
      this.saving = true;
      this.error = null;
      try {
        const { data } = await axios.post('/api/manufacturing/repacks', {
          product_id: this.productId,
          production_order_id: this.orderId || undefined,
          lines: this.lines.filter(p => Number(p.quantity) > 0).map(p => ({ pack_size_id: p.id, quantity: Number(p.quantity) })),
          notes: this.notes || null,
        });
        this.$toasts.push({ type: 'success', message: data.message });
        this.$emit('done', data.repack);
        this.notes = '';
        this.lines.forEach(l => { l.quantity = null; });
        await this.loadOptions();
      } catch (e) {
        this.error = this.message(e);
      } finally {
        this.saving = false;
      }
    },

    async finishPacking() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/manufacturing/production-orders/${this.orderId}/finish-packing`, {
          leftover: this.order.has_sessions && this.order.remaining > 0 ? this.finish.leftover : 'keep',
          reason: this.finish.reason || null,
        });
        this.$toasts.push({ type: 'success', message: data.message });
        this.$emit('done', null);
        this.$refs.modal.hide();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.message(e) });
      } finally {
        this.saving = false;
      }
    },

    // ── Add a pack size on the spot ──
    searchProducts(which) {
      const a = this.adding;
      const q = (which === 'kit' ? a.kitSearch : a.search || '').trim();
      if (which === 'product') a.product_id = null;
      if (q.length < 2) { a[which === 'kit' ? 'kitSuggestions' : 'suggestions'] = []; return; }
      clearTimeout(this.kitTimer);
      this.kitTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/products', { params: { search: q, per_page: 10 } });
          a[which === 'kit' ? 'kitSuggestions' : 'suggestions'] = data.data || data;
        } catch (e) {}
      }, 300);
    },
    pickProduct(s) {
      Object.assign(this.adding, { product_id: s.id, search: s.product, suggestions: [] });
    },
    pickKit(s) {
      if (!this.adding.kit.some(k => k.product_id === s.id)) this.adding.kit.push({ product_id: s.id, name: s.product, quantity: 1 });
      Object.assign(this.adding, { kitSearch: '', kitSuggestions: [] });
    },
    async saveSize() {
      const a = this.adding;
      a.saving = true;
      a.error = null;
      try {
        let giId = this.order && this.order.grouped_inventory_id;
        if (!giId) {
          const first = this.options.pack_sizes[0];
          giId = first && first.grouped_inventory_id;
        }
        if (a.editId) {
          await axios.put(`/api/manufacturing/pack-sizes/${a.editId}`, {
            content_quantity: a.content_quantity, content_unit: this.unit,
            components: a.kit.map(k => ({ product_id: k.product_id, quantity: Number(k.quantity) || 1 })),
          });
          this.adding = this.emptyAdding();
          await this.loadOptions();
          return;
        }
        await axios.post('/api/manufacturing/pack-sizes', {
          // No pack size yet: the server finds the recipe from the loose product.
          grouped_inventory_id: giId || undefined, output_product_id: giId ? undefined : this.productId,
          product_id: a.product_id,
          content_quantity: a.content_quantity, content_unit: this.unit,
          components: a.kit.map(k => ({ product_id: k.product_id, quantity: Number(k.quantity) || 1 })),
        });
        this.adding = this.emptyAdding();
        await this.loadOptions();
      } catch (e) {
        a.error = this.message(e);
      } finally {
        a.saving = false;
      }
    },

    editSize(p) {
      this.adding = {
        ...this.emptyAdding(), open: true, editId: p.id, product_id: p.product_id,
        search: p.product && p.product.product, content_quantity: Number(p.content_quantity),
        kit: this.kitOf(p).map(k => ({ product_id: k.product_id, name: k.name, quantity: k.quantity })),
      };
    },
    async removeSize(p) {
      try {
        await axios.delete(`/api/manufacturing/pack-sizes/${p.id}`);
        this.$toasts.push({ type: 'success', message: 'Pack size removed.' });
        await this.loadOptions();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.message(e) });
      }
    },

    startReverse(r) {
      this.reversing = r.id;
      this.reverseReason = '';
    },

    async reverse(r) {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/manufacturing/repacks/${r.id}/reverse`, { reason: this.reverseReason });
        this.$toasts.push({ type: 'success', message: data.message });
        this.$emit('done', data.repack);
        await this.loadOptions();
      } catch (e) {
        this.$toasts.push({ type: 'danger', message: this.message(e) });
      } finally {
        this.saving = false;
        this.reversing = null;
      }
    },

    message(e) {
      const d = e && e.response && e.response.data;
      return d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Request failed.');
    },
    fmt(n) {
      return Math.round(Number(n || 0) * 1000) / 1000;
    },
    money(n) {
      return Number(n || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      return moment(d).format('DD MMM YYYY HH:mm');
    },
  },
};
</script>
